/**
 * ChangeEmailScreen.jsx — TEST 3.01: Settings -> Email. The account e-mail changes only after a code sent to the
 * NEW address is entered.
 *
 *   step 1 (form): the new address + "confirm it's you" (components/ContactReauth: the password, or a fresh
 *                  Apple / Google sign-in) -> POST /auth/contact/email/code -> { changeToken } (15 minutes)
 *   step 2 (code): the 6-digit code -> POST /auth/contact/email/verify -> { email, token }
 *   done:          authAPI.emailChangeVerify has already replaced the stored session with `token` (the server
 *                  bumped token_version, so the old one is dead); the user store's e-mail is updated here.
 * Every refusal code has its own sentence (utils/contactChange CONTACT_REFUSAL_KEYS).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check } from 'lucide-react'
import useMenuBack from '../../hooks/useMenuBack'
import { ResponsiveWrapper } from '../../components/layout'
import { authAPI } from '../../services/api'
import { useI18n } from '../../i18n'
import { useUserStore } from '../../stores'
import ContactReauth, { useContactReauth } from '../../components/ContactReauth'
import { LockNotice, useLockCountdown } from '../../components/CurrentFactorField'
import { isProviderCancel, providerName } from '../../utils/ssoReauth'
import { apiErrorCode } from '../../utils/apiErrorMessage'
import { contactRefusal, emailShaped, RESEND_SECONDS } from '../../utils/contactChange'

const INPUT = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none'
const PRIMARY = { width: '100%', padding: '13px 16px', borderRadius: 14, border: 'none', fontSize: 15, fontWeight: 700, color: 'white' }

export default function ChangeEmailScreen() {
  const { t } = useI18n()
  const goBack = useMenuBack()
  const navigate = useNavigate()
  const reauth = useContactReauth()
  const [step, setStep] = useState('form')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [changeToken, setChangeToken] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [resendIn, setResendIn] = useState(0)
  const [lockSeconds, startLock] = useLockCountdown()
  const [doneEmail, setDoneEmail] = useState('')
  // R301P L3: the field at fault takes the focus after an error, once the screen is not busy (inputs are disabled then).
  const emailField = useRef(null)
  const codeField = useRef(null)
  const [focusAsk, setFocusAsk] = useState(null)
  const askFocus = (field) => setFocusAsk((p) => ({ field, n: (p?.n || 0) + 1 }))
  useEffect(() => {
    if (!focusAsk || busy) return
    const el = focusAsk.field === 'code' ? codeField.current : emailField.current
    if (el && !el.disabled) el.focus()
    setFocusAsk(null)
  }, [focusAsk, busy])

  useEffect(() => {
    if (!resendIn) return undefined
    const id = setTimeout(() => setResendIn((s) => (s > 1 ? s - 1 : 0)), 1000)
    return () => clearTimeout(id)
  }, [resendIn])

  /** Shows the refusal's sentence and returns what contactRefusal read (null = not a contact refusal). */
  const showRefusal = (e, opts) => {
    const r = contactRefusal(e, opts)
    // R301P L2: an unknown code, or a 401 with none, shows the translated sentence and the status, never the server's text.
    if (!r) { setError(`${t('errors2.somethingWentWrong')}${apiErrorCode(e)}`); return null }
    if (r.kind === 'locked') { startLock(r.seconds); setError(null); return r }
    if (r.kind === 'reauth') reauth.refused(r)
    setError(t(r.key))
    return r
  }

  const send = async () => {
    const next = email.trim()
    if (busy || lockSeconds || !emailShaped(next) || !reauth.ready) return
    setBusy(true); setError(null)
    try {
      const proof = await reauth.prove()
      const res = await authAPI.emailChangeCode(next, proof)
      setChangeToken(res?.changeToken || null)
      setCode('')
      setStep('code')
      setResendIn(RESEND_SECONDS)
    } catch (e) {
      if (isProviderCancel(e) || e?.message === 'cancelled') setError(t('settingsMisc.reauthCancelled'))
      else {
        const r = showRefusal(e)
        // A refused re-auth is answered on the first step, where its field is.
        if (r && (r.kind === 'reauth' || r.kind === 'locked')) setStep('form')
        if (!r || r.kind === 'key') askFocus(step === 'code' ? 'code' : 'email')
      }
    } finally {
      setBusy(false)
    }
  }

  /**
   * R301P L1: the password is not kept after a send, so a resend with the password method goes back to the first
   * step (the address stays) and asks for it again. An Apple / Google resend is a fresh sign-in inside this tap.
   */
  const resend = () => {
    if (reauth.ready) { send(); return }
    setError(null); setCode(''); setStep('form')
  }

  const verify = async () => {
    if (busy || !changeToken || !/^\d{6}$/.test(code)) return
    setBusy(true); setError(null)
    try {
      const res = await authAPI.emailChangeVerify(changeToken, code)
      const now = res?.email || email.trim()
      const st = useUserStore.getState()
      if (st.user) st.setUser({ ...st.user, email: now })
      setDoneEmail(now)
      setStep('done')
    } catch (e) {
      const r = showRefusal(e, { atVerify: true })
      setCode('')
      if (r && r.kind === 'restart') { setChangeToken(null); setStep('form') }
      if (r?.kind !== 'reauth' && r?.kind !== 'locked') askFocus(r?.kind === 'restart' ? 'email' : 'code')
    } finally {
      setBusy(false)
    }
  }

  if (step === 'done') {
    return (
      <ResponsiveWrapper className="bg-white shadow-xl">
        <div className="screen-min-h bg-white flex flex-col items-center justify-center p-8" data-contact-done="email">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-green-600" strokeWidth={2.5} />
          </div>
          <p className="text-gray-700 text-center font-semibold">{t('contact.emailDone', { email: doneEmail })}</p>
          <p className="text-gray-500 text-center text-sm mt-3">{t('contact.signedOutElsewhere')}</p>
          {reauth.linkedSso.map((p) => (
            <p key={p} className="text-gray-500 text-center text-sm mt-2">{t('contact.ssoStillWorks', { provider: providerName(p) })}</p>
          ))}
          <button type="button" onClick={() => navigate('/settings', { replace: true })} style={{ ...PRIMARY, background: '#46A8F9', marginTop: 24 }}>
            {t('common.done')}
          </button>
        </div>
      </ResponsiveWrapper>
    )
  }

  const canSend = !busy && !lockSeconds && emailShaped(email) && reauth.ready
  return (
    <ResponsiveWrapper className="bg-white shadow-xl">
      <div className="screen-min-h bg-white flex flex-col">
        <div className="px-4 pt-4 pb-2 flex items-center gap-3">
          <button type="button" onClick={() => goBack()} className="p-2 -ml-2 text-gray-600" aria-label={t('common.back')}>
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold text-orella-700">{t('contact.emailTitle')}</h1>
        </div>
        <div className="flex-1 px-4 pb-8 overflow-auto">
          {step === 'form' ? (
            <>
              <p className="text-sm text-gray-500 mt-1 mb-5">{t('contact.emailIntro')}</p>
              <label htmlFor="contact-new-email" className="block text-sm text-gray-500 mb-1.5">{t('contact.newEmail')}</label>
              <input id="contact-new-email" ref={emailField} type="email" inputMode="email" autoComplete="email" value={email} disabled={busy}
                onChange={(e) => { setEmail(e.target.value); setError(null) }} className={INPUT} />
              <ContactReauth reauth={reauth} disabled={busy} />
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mt-1 mb-5">{t('contact.enterEmailCode', { email: email.trim() })}</p>
              <label htmlFor="contact-email-code" className="block text-sm text-gray-500 mb-1.5">{t('onboarding.phone.codeLabel')}</label>
              <input id="contact-email-code" ref={codeField} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} disabled={busy}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null) }} className={INPUT} />
              <button type="button" onClick={resend} disabled={busy || resendIn > 0 || !!lockSeconds}
                style={{ background: 'none', border: 0, color: '#64748B', fontSize: 13, textDecoration: resendIn ? 'none' : 'underline', padding: '8px 0' }}>
                {resendIn > 0 ? `${t('auth.resendIn')} ${resendIn}s` : t('auth.resendCode')}
              </button>
            </>
          )}
          <div style={{ marginTop: 12 }}><LockNotice seconds={lockSeconds} /></div>
          {error && <p role="alert" style={{ fontSize: 12.5, color: '#DC2626', marginTop: 8 }}>{error}</p>}
          <div style={{ marginTop: 20 }}>
            {step === 'form' ? (
              <button type="button" onClick={send} disabled={!canSend} style={{ ...PRIMARY, background: canSend ? '#46A8F9' : '#A5D4FC' }}>
                {t('contact.sendCode')}
              </button>
            ) : (
              <button type="button" onClick={verify} disabled={busy || !/^\d{6}$/.test(code)}
                style={{ ...PRIMARY, background: !busy && /^\d{6}$/.test(code) ? '#46A8F9' : '#A5D4FC' }}>
                {t('contact.confirmEmail')}
              </button>
            )}
          </div>
        </div>
      </div>
    </ResponsiveWrapper>
  )
}
